import { formatFileSize } from 'shared/utils'

import type { Message, RenderContent } from '../../model/types'

import { api } from '../../api'
import { findRenderContent } from '../../utils/utils'
import css from './Message.module.scss'

const BLOB_TYPES: (keyof RenderContent)[] = ['file']

const findLinks = (text: string) => {
	const regex = /((https?|ftp):\/\/[^\s/$.?#].[^\s]*)/gi
	const elements = text
		.split(regex)
		.filter(element => element !== 'https' && element !== 'http')
	return elements.map((element, index) => {
		if (regex.test(element)) {
			return (
				<a
					className='mailbox-link'
					href={element}
					key={index}
					rel='noreferrer'
					target='_blank'
				>
					{element}
				</a>
			)
		}
		return <span key={index}>{element}</span>
	})
}

export function MessageBody(message: Message) {
	const renderKey = findRenderContent(message.render_content)!
	const content = message.render_content[0]?.[renderKey]
	const attachQuery = api.useAttachmentQuery(
		{
			accepts: content?.recommended_accept_header ?? '',
			id: message.message_id,
			url: content?.url ?? ''
		},
		{
			skip: !content || !BLOB_TYPES.includes(renderKey)
		}
	)

	let url = content?.url ?? ''

	if (attachQuery.isSuccess) {
		url = attachQuery.data
	}

	if (message.render_format === 'RECALLED') {
		return (
			<p className='mailbox-deleted-message'>This message has been deleted.</p>
		)
	}
	const isEditedMessage = message.render_format === 'EDITED'
	const renderedText = (
		<p style={{ whiteSpace: 'pre-wrap' }}>
			{findLinks(message.text)}{' '}
			{isEditedMessage && <span style={{ color: '#565657' }}>(Edited)</span>}
		</p>
	)

	if (!renderKey) {
		return renderedText
	}

	switch (renderKey) {
		case 'audio':
			return (
				<div>
					{message.text && (
						<div className='attachment-text'>{renderedText}</div>
					)}
					<audio controls src={url} />
				</div>
			)

		case 'external_media':
			return (
				<>
					{message.text && (
						<div className='attachment-text'>{renderedText}</div>
					)}
					<img alt='gif' src={url} />
				</>
			)

		case 'file':
			return (
				<div>
					{message.text && (
						<div className='attachment-text'>{renderedText}</div>
					)}
					<a
						className={css.file}
						download
						href={url}
						rel='noopener noreferrer'
						target='_blank'
					>
						<span className={css.fileName}>{content?.name}</span>
						{formatFileSize(content?.byte_size ?? 0)}
					</a>
				</div>
			)

		case 'vector_image':
			return (
				<div>
					{message.text && (
						<div className='attachment-text'>{renderedText}</div>
					)}

					<img
						alt='chat'
						className={css.image}
						height={190}
						src={url}
						width={200}
					/>
				</div>
			)

		case 'video_meeting':
			return (
				<div>
					<p className='attachment-text warning-text'>
						Video meetings are not supported
					</p>
				</div>
			)

		default:
			return <p>{message.text}</p>
	}
}
